import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concat, EMPTY } from "rxjs";
import { catchError, mergeMap, switchMap } from "rxjs/operators";
import { RoomStageLayoutActions } from "../actions";
import {
  RoomStageLayoutFocusDeleteRequestDTO,
  RoomStageLayoutFocusSetRequestDTO,
  RoomStageLayoutMainDeleteRequestDTO,
  RoomStageLayoutMainSetRequestDTO,
  RoomStageLayoutUpdateRequestDTO,
  RoomStreamActiveSetRequestDTO
} from "../dto";
import { RoomStageLayoutService, RoomStreamService } from "../services";

@Injectable()
export class RoomStageLayoutEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly roomStageLayoutService: RoomStageLayoutService,
    private readonly roomStreamService: RoomStreamService
  ) {}

  roomStageLayoutFocusDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStageLayoutActions.roomStageLayoutFocusDelete),
      switchMap(({ roomStageLayoutState, roomStageLayoutFocusDeleteCmd }) =>
        this.roomStageLayoutService
          .roomStageLayoutFocusDelete(
            new RoomStageLayoutFocusDeleteRequestDTO({
              roomId: roomStageLayoutFocusDeleteCmd.cmd.roomId
            })
          )
          .pipe(
            mergeMap(() => [
              RoomStageLayoutActions.roomStageLayoutFocusDeleteSuccess({
                roomStageLayoutFocusDeleteCmd
              })
            ]),
            catchError((_: Error) => [
              RoomStageLayoutActions.roomStageLayoutFocusDeleteFailure({
                roomStageLayoutState,
                error: _.message
              })
            ])
          )
      )
    )
  );

  roomStageLayoutFocusSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStageLayoutActions.roomStageLayoutFocusSet),
      switchMap(({ roomStageLayoutState, roomStageLayoutFocusSetCmd }) =>
        concat(
          // Stream activation may cause layout change,
          // do it only for a not active stream.
          roomStageLayoutFocusSetCmd.cmd.streamIsActive
            ? EMPTY
            : this.roomStreamService.roomStreamActiveSet(
                new RoomStreamActiveSetRequestDTO({
                  roomId: roomStageLayoutFocusSetCmd.cmd.roomId,
                  streamId: roomStageLayoutFocusSetCmd.cmd.streamId
                })
              ),
          this.roomStageLayoutService.roomStageLayoutFocusSet(
            new RoomStageLayoutFocusSetRequestDTO({
              roomId: roomStageLayoutFocusSetCmd.cmd.roomId,
              streamId: roomStageLayoutFocusSetCmd.cmd.streamId
            })
          )
        ).pipe(
          mergeMap((_) => [
            RoomStageLayoutActions.roomStageLayoutFocusSetSuccess({
              roomStageLayoutFocusSetCmd: _
                ? roomStageLayoutFocusSetCmd
                : undefined,
              roomStageLayoutState
            })
          ]),
          catchError((_: Error) => [
            RoomStageLayoutActions.roomStageLayoutFocusSetFailure({
              roomStageLayoutState,
              error: _.message
            })
          ])
        )
      )
    )
  );

  roomStageLayoutMainDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStageLayoutActions.roomStageLayoutMainDelete),
      switchMap(({ roomStageLayoutState, roomStageLayoutMainDeleteCmd }) =>
        this.roomStageLayoutService
          .roomStageLayoutMainDelete(
            new RoomStageLayoutMainDeleteRequestDTO({
              roomId: roomStageLayoutMainDeleteCmd.cmd.roomId
            })
          )
          .pipe(
            mergeMap(() => [
              RoomStageLayoutActions.roomStageLayoutMainDeleteSuccess({
                roomStageLayoutMainDeleteCmd
              })
            ]),
            catchError((_: Error) => [
              RoomStageLayoutActions.roomStageLayoutMainDeleteFailure({
                roomStageLayoutState,
                error: _.message
              })
            ])
          )
      )
    )
  );

  roomStageLayoutMainSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStageLayoutActions.roomStageLayoutMainSet),
      switchMap(({ roomStageLayoutState, roomStageLayoutMainSetCmd }) =>
        concat(
          // Stream activation may cause layout change,
          // do it only for a not active stream.
          roomStageLayoutMainSetCmd.cmd.streamIsActive
            ? EMPTY
            : this.roomStreamService.roomStreamActiveSet(
                new RoomStreamActiveSetRequestDTO({
                  roomId: roomStageLayoutMainSetCmd.cmd.roomId,
                  streamId: roomStageLayoutMainSetCmd.cmd.streamId
                })
              ),
          this.roomStageLayoutService.roomStageLayoutMainSet(
            new RoomStageLayoutMainSetRequestDTO({
              roomId: roomStageLayoutMainSetCmd.cmd.roomId,
              streamId: roomStageLayoutMainSetCmd.cmd.streamId
            })
          )
        ).pipe(
          mergeMap((_) => [
            RoomStageLayoutActions.roomStageLayoutMainSetSuccess({
              roomStageLayoutMainSetCmd: _
                ? roomStageLayoutMainSetCmd
                : undefined,
              roomStageLayoutState
            })
          ]),
          catchError((_: Error) => [
            RoomStageLayoutActions.roomStageLayoutMainSetFailure({
              roomStageLayoutState,
              error: _.message
            })
          ])
        )
      )
    )
  );

  roomStageLayoutUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStageLayoutActions.roomStageLayoutUpdate),
      switchMap(({ roomStageLayoutState, roomStageLayoutUpdateVO }) =>
        this.roomStageLayoutService
          .roomStageLayoutUpdate(
            new RoomStageLayoutUpdateRequestDTO({
              roomId: roomStageLayoutUpdateVO.value.roomId,
              layout: roomStageLayoutUpdateVO.value.layout
            })
          )
          .pipe(
            mergeMap(() => [
              RoomStageLayoutActions.roomStageLayoutUpdateSuccess({
                roomStageLayoutUpdateVO: roomStageLayoutUpdateVO
              })
            ]),
            catchError((_: Error) => [
              RoomStageLayoutActions.roomStageLayoutUpdateFailure({
                roomStageLayoutState,
                error: _.message
              })
            ])
          )
      )
    )
  );
}
